import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  useMediaQuery,
  Stack,
  Modal,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import backdrop from "./assets/HeaderbgImage.svg";
import demoImage from "./assets/DemoImage.svg";
import CalendarModal from "../CalendarModal/CalendarModal";

// Enhanced ActionButton with better hover effects and sizing
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 9999,
  padding: theme.spacing(1.5, 4),
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease-in-out",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)",
}));

const HeadingPage = () => {
  // Use dynamic height to better handle different screen sizes
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentPath = window.location.pathname;
  const isDarkHome = currentPath.endsWith('/darkhome');
  
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: "90vh", sm: "90vh", md: "95vh" }, 
          maxHeight: { xs: "none", md: "1000px" },
          backgroundImage: 'none',
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center top" },
          overflow: "hidden",
          mb: { xs: "0", sm: "15px", md: "20px" }, 
          borderRadius: { xs: "10px", sm: "15px", md: "20px" },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.4))",
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: "relative",
            py: { xs: 4, sm: 6, md: 8 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 10,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: { xs: 2, sm: 4 },
              mt: { xs: 4, sm: 8, md: 12, lg: 24 },
            }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2rem", sm: "3rem", md: "3.5rem", lg: "4rem" },
                color: "white",
                mb: { xs: 2, sm: 3 },
                lineHeight: { xs: 1.2, sm: 1.25 },
                maxWidth: "100%",
                textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              Agentic AI Platform to Automate <br className="hide-on-mobile" /> Workflows!
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" },
                color: "#E5E7EB",
                maxWidth: { xs: "100%", sm: "85%", md: "75%" },
                mb: { xs: 3, sm: 4, md: 5 },
                opacity: 0.9,
                textShadow: "0px 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              Explore no-code, low-code platform to build and deploy AI agents using any LLM and your existing systems.
            </Typography>

            <Stack
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: "90%", md: "80%" },
                justifyContent: "center",
              }}
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems="center"
            >
              <ActionButton
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  bgcolor: "#000000",
                  "&:hover": {
                    bgcolor: "#333333",
                    transform: "translateY(-2px)",
                    boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.3)",
                  },
                  minWidth: { xs: "160px", sm: "180px", md: "220px" },
                  height: { xs: "48px", sm: "52px", md: "56px" },
                }}
                onClick={() => window.location.href = "https://flow.beanbag.ai/signup"}
              >
                See it in Action
              </ActionButton>

              <Button
                variant="text"
                onClick={handleOpenModal}
                sx={{
                  color: "white",
                  textDecoration: "underline",
                  textTransform: "none",
                  fontSize: { xs: '16px', sm: '18px', md: '20px' },
                  fontWeight: 500,
                  "&:hover": {
                    color: "#f0f0f0",
                    textDecoration: "underline",
                  },
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                Book a Demo
              </Button>
            </Stack>

            <Box
              sx={{
                width: "100%",
                mt: { xs: 4, sm: 5, md: 6 },
                px: { xs: 1, sm: 3, md: 0 },
                position: "relative",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={demoImage}
                alt="Platform Demo"
                sx={{
                  width: "100%",
                  maxWidth: { xs: "95%", sm: "90%", md: "95%", lg: "100%" },
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.25)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />

      <style jsx global>{`
        @media (max-width: 600px) {
          .hide-on-mobile {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default HeadingPage;